import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiEdit3, FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardAPI } from '../../services/realApi';
import CustomizableDashboard from './CustomizableDashboard';

interface DashboardData {
  id: string | number;
  name: string;
  description?: string;
  context?: string;
  layout?: unknown;
  created_by?: string | number;
  is_default?: boolean;
}

const DashboardViewer: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dashboardId) {
      loadDashboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]);

  const loadDashboard = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardAPI.getDashboard(dashboardId!);
      if (result.success && result.data) {
        setDashboard(result.data);
      } else {
        setError(result.error || 'Failed to load dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (): void => {
    navigate(`/hub/dashboards/designer/${dashboardId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
        <button
          onClick={() => navigate('/hub/dashboards')}
          className="mt-4 flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>Back to Marketplace</span>
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  // Extract layout array from dictionary format
  const layoutData = (dashboard.layout as Record<string, unknown>) || {};
  const layout = Array.isArray(layoutData)
    ? layoutData
    : (layoutData.lg as unknown[]) || (layoutData.default as unknown[]) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/hub/dashboards')}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Back to Marketplace</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {dashboard.name}
            </h1>
            {dashboard.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {dashboard.description}
              </p>
            )}
          </div>
          {dashboard.created_by === user?.id && (
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <FiEdit3 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      <CustomizableDashboard
        title={dashboard.name}
        description={dashboard.description || ''}
        storageKey={`dashboard-viewer-${dashboardId}`}
        context={dashboard.context || 'hub'}
        defaultLayout={layout}
      />
    </div>
  );
};

export default DashboardViewer;
